import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import api, { mediaUrl, kr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { ProductCard } from "@/components/ProductCard";
import { HalalBadge } from "@/components/HalalBadge";
import { SlidePlacement } from "@/components/SlidePlacement";
import { BlockList } from "@/components/BlockRenderer";
import { CategoryIcon } from "@/components/CategoryIcon";
import { CampaignStrip } from "@/components/CampaignStrip";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [offers, setOffers] = useState([]);
  const [home, setHome] = useState(null);
  const [video, setVideo] = useState(null);
  const [customLayout, setCustomLayout] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    api.get("/products?limit=20").then(({ data }) => setProducts(data.products)).catch(() => {});
    api.get("/categories?visible_only=true").then(({ data }) => setCats(data.filter((c) => !c.parent_id))).catch(() => {});
    api.get("/offers").then(({ data }) => setOffers(data)).catch(() => {});
    api.get("/homepage").then(({ data }) => setHome(data)).catch(() => {});
    api.get("/video").then(({ data }) => setVideo(data)).catch(() => {});
    api.get("/layouts/home/published").then(({ data }) => setCustomLayout(data.blocks || [])).catch(() => setCustomLayout([])).finally(() => setReady(true));
  }, []);

  const catColors = ["#EC1E79", "#84CC16", "#F97316", "#A855F7", "#22C55E", "#EAB308", "#EF4444"];

  if (!ready) {
    return <div className="min-h-[70vh]" data-testid="home-loading" />;
  }
  if (customLayout && customLayout.length) {
    return <div data-testid="custom-home-layout"><BlockList blocks={customLayout} /></div>;
  }

  return (
    <div>
      <SlidePlacement where="home_top" />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="pointer-events-none absolute -right-20 -top-20 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 left-1/4 h-72 w-72 rounded-full bg-candy-lime/10 blur-3xl" />
        <div className="mx-auto grid max-w-7xl items-center gap-8 px-4 py-14 sm:py-20 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
            <div className="mb-4"><HalalBadge size="lg" /></div>
            <h1 className="text-4xl font-black leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              {home?.hero_title || "BARE 1 MERE"}
              <span className="mt-2 block bg-gradient-to-r from-primary to-candy-purple bg-clip-text text-transparent">
                Slik med sjæl 🍭
              </span>
            </h1>
            <p className="mt-5 max-w-md text-base text-muted-foreground sm:text-lg">
              {home?.hero_subtitle || "Danmarks lækreste udvalg af 100% halal slik, chokolade og godter."}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/produkter" data-testid="hero-cta-button" className="gloss inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 font-bold text-primary-foreground transition-transform hover:scale-105">
                {home?.hero_cta || "Shop nu"} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/tilbud" className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-3.5 font-semibold transition-colors hover:border-primary hover:text-primary">
                🔥 Se tilbud
              </Link>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="relative">
            <div className="animate-floaty overflow-hidden rounded-3xl border border-border/70 lux-shadow-strong">
              <img src={mediaUrl(products[0]?.main_image) || "https://images.unsplash.com/photo-1582058091505-f87a2e55a40f?w=800&q=85"} alt="Slik" className="h-72 w-full object-cover sm:h-96" />
            </div>
          </motion.div>
        </div>
      </section>

      <SlidePlacement where="home_under_hero" />

      {/* KATEGORIER */}
      <CampaignStrip />
      <section className="mx-auto max-w-7xl px-4 py-14">
        <p className="lux-eyebrow mb-1">Find din favorit</p>
        <h2 className="mb-6 text-2xl font-extrabold tracking-tight sm:text-3xl">Kategorier</h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {cats.map((c, i) => (
            <motion.div key={c.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
              <Link
                to={`/kategori/${c.slug}`}
                data-testid={`category-card-${c.slug}`}
                className="group flex h-32 flex-col items-center justify-center gap-2 rounded-2xl border border-border transition-all duration-300 hover:-translate-y-1.5"
                style={{ background: `linear-gradient(160deg, ${catColors[i % catColors.length]}22, transparent)` }}
              >
                <span className="transition-transform duration-300 group-hover:scale-125"><CategoryIcon cat={c} size={48} /></span>
                <span className="font-bold">{c.name}</span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <SlidePlacement where="home_products" />

      {/* AKTUELLE TILBUD */}
      {offers.length > 0 && (
        <section className="border-y border-border bg-gradient-to-b from-secondary/40 to-background py-14">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-2xl font-extrabold sm:text-3xl">🔥 Aktuelle tilbud</h2>
              <Link to="/tilbud" className="flex items-center gap-1 text-sm font-semibold text-primary">Se alle <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {offers.slice(0, 4).map((o, i) => o.product && <ProductCard key={o.id} product={o.product} index={i} />)}
            </div>
          </div>
        </section>
      )}

      <SlidePlacement where="home_offers" />

      {/* VIDEO / REKLAME */}
      {video?.active && (
        <section className="mx-auto max-w-7xl px-4 py-14">
          <div className="relative overflow-hidden rounded-3xl border border-border">
            {video.url ? (
              <video src={mediaUrl(video.url)} autoPlay={video.autoplay} loop={video.loop} muted={video.muted} playsInline className="h-64 w-full object-cover sm:h-96" />
            ) : (
              <img src="https://images.unsplash.com/photo-1635342219731-4ae2bf39e1e9?w=1200&q=85" alt="" className="h-64 w-full object-cover sm:h-96" />
            )}
            <div className="absolute inset-0 flex flex-col items-start justify-end bg-gradient-to-t from-black/80 to-transparent p-8">
              <h3 className="font-brand text-2xl font-bold sm:text-4xl">{video.title}</h3>
              <p className="mt-1 max-w-lg text-sm text-white/80 sm:text-base">{video.text}</p>
              {video.cta_text && (
                <Link to={video.cta_link || "/produkter"} className="gloss mt-4 rounded-full bg-primary px-6 py-3 font-bold text-primary-foreground">{video.cta_text}</Link>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
